import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { X, Star, ZoomIn } from '@mui/icons-material';
import { Eye } from 'lucide-react';

const CaseDetailModal = ({ isOpen, onClose, caseData }) => {
    const [selectedImage, setSelectedImage] = useState(null);
    
    if (!isOpen || !caseData) return null;

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('vi-VN', {
            style: 'currency',
            currency: 'VND'
        }).format(amount);
    };

    const formatDateTime = (dateString) => {
        return new Date(dateString).toLocaleString('vi-VN');
    };

    const formatProductName = (productName) => {
        if (!productName) return 'N/A';
        return productName.replace(/\s*SKU.*$/i, '');
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'pending': return 'text-yellow-600 bg-yellow-50';
            case 'in_progress': return 'text-blue-600 bg-blue-50';
            case 'approved': return 'text-green-600 bg-green-50';
            case 'rejected': return 'text-red-600 bg-red-50';
            default: return 'text-gray-600 bg-gray-50';
        }
    };

    const getStatusText = (status) => {
        switch (status) {
            case 'pending': return 'Chờ duyệt';
            case 'in_progress': return 'Đang làm';
            case 'approved': return 'Hoàn thành';
            case 'rejected': return 'Từ chối';
            default: return 'Không xác định';
        }
    };

    const getDeadlineColor = (deadline) => {
        const today = new Date();
        const deadlineDate = new Date(deadline);
        const diffTime = deadlineDate.getTime() - today.getTime();
        const days = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

        if (days < 0) return 'text-red-600';
        if (days <= 1) return 'text-orange-600';
        if (days <= 3) return 'text-yellow-600';
        return 'text-green-600';
    };

    return (
        <>
            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50"
                onClick={onClose}
            >
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 20 }}
                                         className="bg-white rounded-2xl shadow-2xl p-4 sm:p-6 w-full max-w-4xl max-h-[90vh] overflow-y-auto mx-4"
                    onClick={(e) => e.stopPropagation()}
                >
                                         <div className="flex justify-between items-center mb-4 sm:mb-6">
                         <div className="flex items-center space-x-2">
                             <Eye className="w-5 h-5 sm:w-6 sm:h-6 text-purple-600" />
                             <h3 className="text-lg sm:text-xl font-bold text-gray-900">Chi Tiết Case #{caseData.id}</h3>
                         </div>
                                                 <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
                             <X className="w-5 h-5 sm:w-6 sm:h-6" />
                         </button>
                    </div>

                                         <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 lg:gap-6">
                        {/* Left Column - Thông Tin Cơ Bản */}
                        <div className="space-y-4">
                            <div className="bg-gray-50 rounded-lg p-4">
                                <h4 className="font-semibold text-gray-900 mb-3">Thông Tin Cơ Bản</h4>
                                <div className="space-y-3">
                                    <div className="flex justify-between">
                                        <span className="text-gray-600">Mã đơn hàng:</span>
                                        <span className="font-medium">{caseData.order_id || caseData.id}</span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span className="text-gray-600">Sản phẩm:</span>
                                        <span className="font-medium">{formatProductName(caseData.product_name)}</span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span className="text-gray-600">Hình ảnh sản phẩm:</span>
                                        <div className="w-16 h-16 bg-gray-200 rounded-lg flex items-center justify-center">
                                            <span className="text-gray-500 text-xs">IMG</span>
                                        </div>
                                    </div>
                                    <div className="flex justify-between">
                                        <span className="text-gray-600">Màu sắc:</span>
                                        <span className="font-medium">{caseData.color || 'N/A'}</span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span className="text-gray-600">Trạng thái:</span>
                                        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(caseData.status)}`}>
                                            <span className="ml-1">{getStatusText(caseData.status)}</span>
                                        </span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span className="text-gray-600">Ưu tiên:</span>
                                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-600">
                                            {caseData.priority || 'normal'}
                                        </span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span className="text-gray-600">Chi phí:</span>
                                        <span className="font-semibold text-green-600">
                                            {formatCurrency(caseData.cost_per_design || 0)}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Right Column - Thông Tin Thời Gian */}
                        <div className="space-y-4">
                            <div className="bg-gray-50 rounded-lg p-4">
                                <h4 className="font-semibold text-gray-900 mb-3">Thông Tin Liên Hệ</h4>
                                <div className="space-y-3">
                                    <div className="bg-white rounded-lg p-3">
                                        <h5 className="font-medium text-gray-800 mb-2">Seller</h5>
                                        <div className="space-y-1 text-sm">
                                            <div>Email: {caseData.seller_email || 'N/A'}</div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="bg-gray-50 rounded-lg p-4">
                                <h4 className="font-semibold text-gray-900 mb-3">Thông Tin Thời Gian</h4>
                                <div className="space-y-3">
                                    <div className="flex justify-between">
                                        <span className="text-gray-600">Tạo lúc:</span>
                                        <span className="font-medium">{formatDateTime(caseData.created_at)}</span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span className="text-gray-600">Deadline:</span>
                                        <span className={`font-semibold ${getDeadlineColor(caseData.deadline)}`}>
                                            {formatDateTime(caseData.deadline)}
                                        </span>
                                    </div>
                                    {caseData.received_at && (
                                        <div className="flex justify-between">
                                            <span className="text-gray-600">Nhận lúc:</span>
                                            <span className="font-medium">{formatDateTime(caseData.received_at)}</span>
                                        </div>
                                    )}
                                    {caseData.completed_at && (
                                        <div className="flex justify-between">
                                            <span className="text-gray-600">Hoàn thành:</span>
                                            <span className="font-medium">{formatDateTime(caseData.completed_at)}</span>
                                        </div>
                                    )}
                                    {caseData.approved_at && (
                                        <div className="flex justify-between">
                                            <span className="text-gray-600">Duyệt lúc:</span>
                                            <span className="font-medium">{formatDateTime(caseData.approved_at)}</span>
                                        </div>
                                    )}
                                    {caseData.updated_at && (
                                        <div className="flex justify-between">
                                            <span className="text-gray-600">Cập nhật:</span>
                                            <span className="font-medium">{formatDateTime(caseData.updated_at)}</span>
                                        </div>
                                    )}
                                </div>
                            </div>

                            {caseData.description && (
                                <div className="bg-gray-50 rounded-lg p-4">
                                    <h4 className="font-semibold text-gray-900 mb-3">Mô tả</h4>
                                    <p className="text-gray-700 text-sm">{caseData.description}</p>
                                </div>
                            )}

                            {caseData.requirements && (
                                <div className="bg-gray-50 rounded-lg p-4">
                                    <h4 className="font-semibold text-gray-900 mb-3">Yêu cầu</h4>
                                    <p className="text-gray-700 text-sm">{caseData.requirements}</p>
                                </div>
                            )}

                            {caseData.rating && (
                                <div className="bg-gray-50 rounded-lg p-4">
                                    <h4 className="font-semibold text-gray-900 mb-3">Đánh giá</h4>
                                    <div className="flex items-center space-x-1">
                                        {[1, 2, 3, 4, 5].map((star) => (
                                            <Star key={star} className={`w-4 h-4 ${star <= caseData.rating ? 'text-yellow-500' : 'text-gray-300'}`} />
                                        ))}
                                        <span className="ml-2 text-sm font-semibold">{caseData.rating}/5</span>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>

                    {caseData.note && (
                        <div className="bg-gray-50 rounded-lg p-4 mt-4">
                            <h4 className="font-semibold text-gray-900 mb-3">Ghi Chú</h4>
                            <div className="w-full max-h-64 overflow-y-auto bg-white rounded-lg p-3">
                                <p className="text-gray-700 text-sm whitespace-pre-wrap break-words">{caseData.note}</p>
                            </div>
                        </div>
                    )}

                    {caseData.design_files && caseData.design_files.length > 0 && (
                        <div className="bg-gray-50 rounded-lg p-4 mt-4">
                            <h4 className="font-semibold text-gray-900 mb-3">Design Files</h4>
                                                         <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                                {caseData.design_files.map((file, index) => (
                                    <div key={index} className="relative group">
                                        <div 
                                                                                         className="w-24 h-24 sm:w-32 sm:h-32 rounded-lg cursor-pointer hover:scale-105 transition-all duration-200 relative overflow-hidden"
                                            onClick={() => setSelectedImage({ file, index })}
                                        >
                                            <img
                                                src={file}
                                                alt={`Design ${index + 1}`}
                                                className="w-full h-full object-cover"
                                                onError={(e) => {
                                                    e.target.style.display = 'none';
                                                    e.target.nextSibling.style.display = 'flex';
                                                }}
                                            />
                                            <div className="hidden w-full h-full bg-gray-200 rounded-lg items-center justify-center">
                                                <span className="text-gray-500 text-sm">IMG</span>
                                            </div>
                                            <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 transition-all duration-200 flex items-center justify-center">
                                                <ZoomIn className="text-white opacity-0 group-hover:opacity-100 transition-opacity duration-200" />
                                            </div>
                                        </div>
                                                                                 <div className="absolute top-1 left-1 w-5 h-5 bg-black bg-opacity-70 text-white rounded-full flex items-center justify-center text-xs font-bold">
                                             {index + 1}
                                         </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Action Buttons */}
                    <div className="mt-6 flex justify-end space-x-3">
                        <button
                            onClick={onClose}
                            className="px-4 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400 transition-colors"
                        >
                            Đóng
                        </button>
                    </div>
                </motion.div>
            </motion.div>

            {/* Image Modal */}
            {selectedImage && (
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="fixed inset-0 z-[60] flex items-center justify-center bg-black bg-opacity-75"
                    onClick={() => setSelectedImage(null)}
                >
                    <motion.div
                        initial={{ opacity: 0, scale: 0.8 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0, scale: 0.8 }}
                        className="relative max-w-4xl max-h-[90vh] overflow-hidden rounded-lg"
                        onClick={(e) => e.stopPropagation()}
                    >
                                                 <div className="bg-white p-4 rounded-lg">
                             <div className="flex justify-between items-center mb-4">
                                 <h3 className="text-lg font-semibold text-gray-900">
                                     Design File #{selectedImage.index + 1}
                                 </h3>
                                 <button 
                                     onClick={() => setSelectedImage(null)}
                                     className="text-gray-500 hover:text-gray-700"
                                 >
                                     <X className="w-6 h-6" />
                                 </button>
                             </div>
                             <div className="flex justify-end">
                                 <div className="w-80 h-80 sm:w-96 sm:h-96 rounded-lg overflow-hidden">
                                     <img
                                         src={selectedImage.file}
                                         alt={`Design ${selectedImage.index + 1}`}
                                         className="w-full h-full object-contain"
                                         onError={(e) => {
                                             e.target.src = 'https://via.placeholder.com/384x384?text=Image+Not+Found';
                                         }}
                                     />
                                 </div>
                             </div>
                         </div>
                    </motion.div>
                </motion.div>
            )}
        </>
    );
};

export default CaseDetailModal; 